import { useState, useMemo, useEffect, useRef } from "react";
import { Scissors, LogOut, ChevronDown, PanelLeftClose, PanelLeft, Search, X } from "lucide-react";
import NAV from "../theme/navConfig";
import { useAuth } from "../context/AuthContext";
import T from "../theme/colors";

function NavItem({ item, isActive, collapsed, onNavigate, highlight }) {
  const Icon = item.icon;
  const [hovered, setHovered] = useState(false);

  // When `highlight` is set (search query), bold the matching substring of the label.
  const renderLabel = () => {
    if (!highlight) return item.label;
    const idx = item.label.toLowerCase().indexOf(highlight.toLowerCase());
    if (idx < 0) return item.label;
    const before = item.label.slice(0, idx);
    const match = item.label.slice(idx, idx + highlight.length);
    const after = item.label.slice(idx + highlight.length);
    return (
      <>
        {before}
        <span style={{ background: "var(--primary-dim)" || "rgba(201,82,16,0.18)", borderRadius: 3, padding: "0 1px" }}>
          {match}
        </span>
        {after}
      </>
    );
  };

  return (
    <a
      href={"#" + item.id}
      onClick={(e) => { e.preventDefault(); onNavigate(item.id); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={collapsed ? item.label : ""}
      style={{
        background: isActive
          ? "var(--primary-light)"
          : hovered ? "var(--bg-hover)" : "transparent",
        border: "none", cursor: "pointer",
        display: "flex", alignItems: "center",
        gap: 9, padding: collapsed ? "9px 0" : "7px 12px",
        justifyContent: collapsed ? "center" : "flex-start",
        borderRadius: 7, marginBottom: 1,
        borderLeft: isActive ? `3px solid var(--primary)` : "3px solid transparent",
        transition: "all 0.14s",
        marginLeft: collapsed ? 0 : 4,
        marginRight: collapsed ? 0 : 4,
        width: collapsed ? "100%" : "calc(100% - 8px)",
        textDecoration: "none",
      }}
    >
      <Icon
        size={15}
        style={{
          color: isActive ? "var(--primary)" : hovered ? "var(--text-0)" : "var(--text-1)",
          flexShrink: 0, transition: "color 0.14s",
        }}
      />
      {!collapsed && (
        <span style={{
          fontSize: 15, fontWeight: isActive ? 600 : 400,
          color: isActive ? "var(--primary)" : hovered ? "var(--text-0)" : "var(--text-1)",
          transition: "color 0.14s", whiteSpace: "nowrap",
        }}>
          {renderLabel()}
        </span>
      )}
    </a>
  );
}

export default function Sidebar({ active, onNavigate, collapsed, onToggleCollapse }) {
  const { user, logout, canView, hasPermission } = useAuth();
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  /**
   * Filter NAV by VIEW permission.
   *   - Items without a `module` are always visible (e.g. pre-seed, future tools).
   *   - Groups with zero visible items are hidden entirely.
   * Recomputes whenever the user changes (login/logout or permission refresh).
   */
  const visibleNav = useMemo(() => {
    return NAV
      .map(group => ({
        ...group,
        items: group.items.filter(it =>
          (!it.superOnly || user?.role === "SUPER_ADMIN") &&
          (!it.roles || it.roles.includes(user?.role)) &&
          // `action`: a page that needs more than any tick on its module.
          (!it.module || (it.action ? hasPermission(it.module, it.action) : canView(it.module)))
        ),
      }))
      .filter(group => group.items.length > 0);
  }, [canView, hasPermission, user]);

  /**
   * When `search` is set, narrow the visible nav to items whose label
   * matches (case-insensitive substring).  Empty groups are dropped.
   */
  const q = search.trim().toLowerCase();
  const filteredNav = useMemo(() => {
    if (!q) return visibleNav;
    return visibleNav
      .map(group => ({
        ...group,
        items: group.items.filter(it => it.label.toLowerCase().includes(q)),
      }))
      .filter(group => group.items.length > 0);
  }, [visibleNav, q]);

  const [expandedGroups, setExpandedGroups] = useState(
    Object.fromEntries(NAV.map(g => [g.group, true]))
  );
  const toggleGroup = g => setExpandedGroups(p => ({ ...p, [g]: !p[g] }));

  // Keyboard: Cmd/Ctrl+K focuses the nav search; Esc clears.
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === "Escape" && document.activeElement === searchRef.current) {
        setSearch("");
        searchRef.current?.blur();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Phase 36af — Persist sidebar scroll position across page refreshes.
  // The sidebar grows tall enough to scroll on smaller screens; without
  // this, refresh always snaps it back to top, losing the user's
  // navigation context.  sessionStorage keeps it within the tab session
  // (so opening a new tab starts at top — clean default).
  const navScrollRef = useRef(null);
  const NAV_SCROLL_KEY = "smit:sidebarScrollTop";

  useEffect(() => {
    // Restore on mount
    const saved = sessionStorage.getItem(NAV_SCROLL_KEY);
    if (saved && navScrollRef.current) {
      const px = parseInt(saved, 10);
      if (!isNaN(px) && px > 0) {
        // Defer one frame so layout has settled (group expansion etc.)
        requestAnimationFrame(() => {
          if (navScrollRef.current) navScrollRef.current.scrollTop = px;
        });
      }
    }
  }, []);

  const handleNavScroll = (e) => {
    // Cheap write — sessionStorage is sync but tiny. No throttle needed
    // for typical scroll rates; if profiling shows a hot spot, add one.
    sessionStorage.setItem(NAV_SCROLL_KEY, String(e.currentTarget.scrollTop));
  };

  // Total visible items count (across all groups), used to show "no results".
  const totalVisible = filteredNav.reduce((n, g) => n + g.items.length, 0);

  return (
    <div style={{
      width: collapsed ? 60 : 232, flexShrink: 0,
      // The sidebar sits INSIDE a parent (App.jsx) that is already
      // height-constrained: it's `flex: 1` of a `height: 100vh` root
      // that has a GraceBanner above it.  Using `height: 100vh` here
      // would make the sidebar TALLER than its parent (by the height
      // of the banner) and push the footer — with the Logout button —
      // past the bottom of the visible area.  `height: 100%` matches
      // the actual parent height in every case, mobile or desktop,
      // banner or no banner.  `position: sticky` is also dropped: the
      // parent has `overflow: hidden`, so there's nothing to stick to;
      // it was a no-op that risked compounding the height bug.
      height: "100%",
      background: "var(--bg-sidebar)",
      borderRight: `1px solid var(--border)`,
      display: "flex", flexDirection: "column",
      transition: "width 0.22s cubic-bezier(0.4,0,0.2,1)",
      overflow: "hidden",
      boxShadow: "1px 0 0 rgba(0,0,0,0.04)",
    }}>

      {/* Logo bar */}
      <div style={{
        padding: collapsed ? "14px 0" : "14px 16px",
        borderBottom: `1px solid var(--border)`,
        display: "flex", alignItems: "center",
        justifyContent: collapsed ? "center" : "space-between",
        gap: 8, minHeight: 56, flexShrink: 0,
      }}>
        {!collapsed && (
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 7,
              background: "var(--grad)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 2px 8px rgba(201,82,16,0.28)",
              flexShrink: 0,
            }}>
              <Scissors size={15} style={{ color: "#fff" }} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-0)", fontFamily: "var(--font-display)", lineHeight: 1.1 }}>
                GarmentERP
              </div>
              <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 1 }}>SM IT Solutions</div>
            </div>
          </div>
        )}
        {collapsed && (
          <div style={{
            width: 30, height: 30, borderRadius: 7, background: "var(--grad)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 8px rgba(201,82,16,0.28)",
          }}>
            <Scissors size={14} style={{ color: "#fff" }} />
          </div>
        )}
        <button
          onClick={onToggleCollapse}
          style={{
            background: "none", border: "none", color: "var(--text-2)", cursor: "pointer",
            padding: 4, borderRadius: 5, display: "flex", transition: "color 0.14s",
            flexShrink: 0,
          }}
          onMouseEnter={e => (e.currentTarget.style.color = "var(--text-0)")}
          onMouseLeave={e => (e.currentTarget.style.color = "var(--text-2)")}
        >
          {collapsed ? <PanelLeft size={15} /> : <PanelLeftClose size={15} />}
        </button>
      </div>

      {/* Nav search — only shown when sidebar is expanded */}
      {!collapsed && (
        <div style={{
          padding: "10px 12px", borderBottom: `1px solid var(--border)`,
          flexShrink: 0,
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 7,
            background: "var(--bg-card)", border: `1px solid var(--border)`,
            borderRadius: 7, padding: "6px 9px",
          }}>
            <Search size={12} style={{ color: "var(--text-2)", flexShrink: 0 }} />
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search pages…"
              style={{
                background: "none", border: "none", color: "var(--text-0)",
                fontSize: 14, width: "100%", fontFamily: "var(--font-ui)",
                outline: "none", padding: 0,
              }}
            />
            {search ? (
              <button
                onClick={() => { setSearch(""); searchRef.current?.focus(); }}
                title="Clear"
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  padding: 0, display: "flex", color: "var(--text-2)", flexShrink: 0,
                }}
              >
                <X size={12} />
              </button>
            ) : (
              <span style={{
                fontSize: 10, color: "var(--text-2)", fontFamily: "var(--font-ui)",
                border: `1px solid var(--border)`, borderRadius: 3,
                padding: "1px 4px", flexShrink: 0,
              }}
                title="Press Ctrl+K (or ⌘K) to focus this search">
                ⌘K
              </span>
            )}
          </div>
        </div>
      )}

      {/* Nav scroll area */}
      <div ref={navScrollRef} onScroll={handleNavScroll}
        style={{
          // flex:1 + minHeight:0 is the canonical pattern for a flex
          // child that needs to scroll: without minHeight:0 the child
          // will refuse to shrink below its intrinsic content height,
          // and the footer (Logout) gets pushed past the bottom edge
          // when the page list is long.  With it, the nav truly fills
          // the remaining space and scrolls internally.
          flex: 1, minHeight: 0,
          overflowY: "auto", padding: "8px 0",
        }}>
        {totalVisible === 0 && q && (
          <div style={{
            padding: "16px 14px", textAlign: "center",
            color: "var(--text-2)", fontSize: 14,
          }}>
            No pages match <strong style={{ color: "var(--text-1)" }}>"{search}"</strong>
          </div>
        )}
        {filteredNav.map(group => {
          // When searching, force-expand every group so matches are visible.
          const isExpanded = q ? true : expandedGroups[group.group];
          return (
            <div key={group.group} style={{ marginBottom: 2 }}>
              {!collapsed ? (
                <button
                  onClick={() => !q && toggleGroup(group.group)}
                  style={{
                    width: "100%", background: "none", border: "none",
                    cursor: q ? "default" : "pointer", display: "flex", alignItems: "center",
                    justifyContent: "space-between", padding: "5px 16px", marginBottom: 2,
                  }}
                >
                  <span style={{
                    fontSize: 12, fontWeight: 700, color: "var(--text-2)",
                    letterSpacing: "0.09em", textTransform: "uppercase",
                    fontFamily: "var(--font-ui)",
                  }}>
                    {group.group}
                  </span>
                  {!q && (
                    <ChevronDown size={11} style={{
                      color: "var(--text-2)", transition: "transform 0.18s",
                      transform: isExpanded ? "rotate(0)" : "rotate(-90deg)",
                    }} />
                  )}
                </button>
              ) : (
                <div style={{ height: 1, background: "var(--border)", margin: "5px 10px" }} />
              )}
              {(isExpanded || collapsed) && group.items.map(item => (
                <NavItem
                  key={item.id} item={item}
                  isActive={active === item.id}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                  highlight={q || ""}
                />
              ))}
            </div>
          );
        })}
      </div>

      {/* User / Logout */}
      <div style={{ borderTop: `1px solid var(--border)`, padding: collapsed ? "8px 0" : "8px", flexShrink: 0 }}>
        {!collapsed && user && (
          <div style={{
            padding: "8px 12px", borderRadius: 7, marginBottom: 4,
            background: "var(--bg-hover)", display: "flex", alignItems: "center", gap: 9,
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%", background: "var(--grad)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0,
              fontFamily: "var(--font-ui)",
            }}>
              {user.fullName?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "??"}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-0)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user.fullName || user.username}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-2)" }}>{user.role?.replace(/_/g, " ")}</div>
            </div>
          </div>
        )}
        <button
          onClick={logout}
          style={{
            width: "100%", background: "none", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 9,
            padding: collapsed ? "9px 0" : "8px 12px",
            justifyContent: collapsed ? "center" : "flex-start",
            borderRadius: 7, transition: "background 0.14s, color 0.14s",
            color: "var(--text-1)", fontSize: 15,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--danger-bg)"; e.currentTarget.style.color = "var(--danger)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "var(--text-1)"; }}
        >
          <LogOut size={14} style={{ flexShrink: 0 }} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
}